import {
  Button,
  SearchFilterSelect,
  ShareLinks,
  ToolFilterSelect
} from '@paction-2.0/ui';

import Image from 'next/image';
import Link from 'next/link';

import { AdultIcon } from './icons/AdultIcon';
import { PuppyIcon } from './icons/PuppyIcon';
import { RuralIcon } from './icons/RuralIcon';
import { UrbanIcon } from './icons/UrbanIcon';

import styles from './resultsPanel.module.scss';
import EmailCapture from './EmailCapture';
import type { ResultsPanelProps } from './resultsPanel.types';
import { useResultsPanelControls } from './useResultsPanelControls';
import { useResultsPanelViewModel } from './useResultsPanelViewModel';

const toggleIcons = {
  puppy: PuppyIcon,
  adult: AdultIcon,
  urban: UrbanIcon,
  rural: RuralIcon
};

const ResultsPanel = ({
  inputs,
  setInputs,
  breeds,
  calculatorInputs,
  hasResults,
  resultsList,
  resultsTitle,
  handleClear,
  handleGetStarted,
  handleCopyShare,
  handleEmailSubmit,
  isSaving
}: ResultsPanelProps) => {
  const {
    hasSelectedBreed,
    selectedBreed,
    breedOptions,
    calculatorImageUrl,
    resultsInputs,
    displayedTotals
  } = useResultsPanelViewModel({
    inputs,
    breeds,
    calculatorInputs,
    hasResults,
    resultsList
  });

  const { handleBreedSelect, filters } = useResultsPanelControls({
    inputs,
    setInputs,
    breeds,
    resultsInputs
  });

  return (
    <section className={styles.resultsCard} aria-label="Calculator results">
      <h2 className={styles.resultsHeader}>{resultsTitle}</h2>
      <div className={styles.resultsContainer}>
        <div className={styles.resultsInputs}>
          {calculatorInputs?.length ? (
            <div className={styles.resultsFilters}>
              <div
                key="results-breed"
                className={`${styles.selectorBlock} ${styles.breedSelector}`}
              >
                <div id="breed-label" className={styles.breedLabel}>
                  Select your ideal breed
                </div>
                <SearchFilterSelect
                  variant="legacy"
                  options={breedOptions}
                  value={
                    typeof inputs.breed === 'string' ? inputs.breed : undefined
                  }
                  placeholder="Select a breed"
                  ariaLabelledby="breed-label"
                  allowClear
                  onSelect={handleBreedSelect}
                />
              </div>

              {filters.map((filter) => {
                if (filter.type === 'toggle') {
                  return (
                    <div key={filter.key} className={styles.selectorBlock}>
                      <div className={styles.selectorLabel}>{filter.label}</div>
                      <div className={styles.toggleRow}>
                        {filter.options.map((option) => {
                          const Icon = toggleIcons[option.icon];
                          const isSelected = option.isSelected;

                          return (
                            <button
                              key={option.key}
                              type="button"
                              className={`${styles.toggleOption} ${
                                isSelected ? styles.toggleOptionSelected : ''
                              }`}
                              aria-pressed={isSelected}
                              onClick={option.onSelect}
                            >
                              <div className={styles.toggleIconWrap}>
                                <Icon className={styles.toggleIcon} />
                              </div>
                              <div
                                className={`${styles.toggleTitle} ${
                                  isSelected ? styles.toggleTitleSelected : ''
                                }`}
                              >
                                {option.label}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                return (
                  <ToolFilterSelect
                    key={filter.key}
                    label={filter.label}
                    options={filter.options}
                    value={filter.value}
                    onChange={filter.onChange}
                  />
                );
              })}
            </div>
          ) : null}
          <div className={styles.resultsButtons}>
            {hasResults ? (
              <Button
                type="button"
                onClick={handleClear}
                className={styles.calculateButton}
              >
                Clear
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleGetStarted}
                className={styles.calculateButton}
                disabled={!hasSelectedBreed}
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
        <div className={styles.resultsSummary}>
          {hasResults ? (
            <div className={styles.resultsDisplay}>
              <div className={styles.resultsDisplayIcon}>
                <Image
                  className={styles.resultsDisplayIconImg}
                  src={calculatorImageUrl}
                  alt="calculator"
                  width={240}
                  height={240}
                  sizes="240px"
                />
              </div>
              <div className={styles.resultsDisplayValues}>
                {resultsList.map((result) => (
                  <div key={result.id} className={styles.resultsDisplayValue}>
                    <div className={styles.resultsDisplayHeading}>
                      {result.label}
                    </div>
                    <div>{`$${displayedTotals[result.id]?.monthly ?? '0.00'}`}</div>
                    <div className={styles.resultsDisplayLabel}>monthly</div>
                    <div>{`$${displayedTotals[result.id]?.annual ?? '0.00'}`}</div>
                    <div className={styles.resultsDisplayLabel}>annually</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.resultsPlaceholder}>
              <div className={styles.resultsPlaceholderIcon}>
                <Image
                  className={styles.resultsPlaceholderImg}
                  src={calculatorImageUrl}
                  alt="calculator"
                  width={544}
                  height={544}
                  sizes="544px"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <ShareLinks
        className={styles.resultsShare}
        onlyCopy
        onCopy={handleCopyShare}
      />
      {hasResults ? (
        <EmailCapture onSubmit={handleEmailSubmit} isSubmitting={isSaving} />
      ) : null}
      {hasResults && selectedBreed?.slug && (
        <Link
          href={`/dog-breeds/${selectedBreed.slug}/cost`}
          className={styles.costGuideLink}
        >
          See the full {selectedBreed.name} cost breakdown
        </Link>
      )}
    </section>
  );
};

export default ResultsPanel;
